import React, { Fragment, useState, } from 'react';
import { Button, } from 'reactstrap';
import { isEmpty, orderBy, get, pick, omit, uniq, uniqBy, keyBy, groupBy, chunk, } from 'lodash';
import { toast, } from 'react-toastify';
import { useCounter, useToggle, } from 'react-use';
import { Link } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import classnames from 'classnames';
import numeral from 'numeral';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import fileDownload from 'js-file-download';

import firebase from '../../firebase';
import { isMobile } from '../../util';
import { batch, getDocumentData, getCollectionData, } from '../../shared/firebase';
import { idWithTenant, serial, } from '../../shared/util';
import { fieldDisplayValue, } from '../../shared/util';
import { statuses, } from '../../shared/models/inventory';
import { newId } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ReceivementFormModal from '../modals/ReceivementFormModal';
import ShelvingModal from '../modals/ShelvingModal';
import DirectShelvingModal from '../modals/DirectShelvingModal';
import RevertingShelfModal from '../modals/RevertingShelfModal';
import LocationTransferModal from '../modals/LocationTransferModal';
import BatchRevertingShelfModal from '../modals/BatchRevertingShelfModal';
import QueryInput from '../QueryInput';
import QuerySelector from '../QuerySelector';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ProgressButton from '../ProgressButton';
import ImportButton from '../ImportButton';
import TenantTags from '../TenantTags';

const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const tagsRef = db.collection('tags');
const inventoriesRef = db.collection('inventories');
const locationsRef = db.collection('locations');

export default AdminPage(function AdminReceivements (props) {
  const { tenantIds: tenantIdsForFilter = [], requestIds: requestIdsForFilter = [], productIds: productIdsForFilter = [], } = useQueryParams();
  const [tenantIdForFilter] = tenantIdsForFilter;

  let filteredInventoriesRef = inventoriesRef.where('locationId', '==', null);
  if(!isEmpty(tenantIdForFilter)) {
    filteredInventoriesRef = filteredInventoriesRef.where('tenantId', '==', tenantIdForFilter);
  } else {
    filteredInventoriesRef = filteredInventoriesRef.where('tenantId', '==', 'dummy');
  }

  const inventories = useCollectionSubscription(!isMobile() && filteredInventoriesRef, [tenantIdForFilter]);
  const requestIdOptions = orderBy(uniqBy(inventories.map(_ => ({ ..._, requestId: _.receivingRequestId || _.combiningRequestId, })), _ => _.requestId), _ => numeral(_.requestId).value(), 'desc').map(_ => [_.tenantId, _.requestId].join('-')).map(_ => ({ label: _, value: _ }));
  const productIdOptions = orderBy(uniqBy(inventories, _ => _.productId), _ => numeral(_.productId).value(), 'desc').map(_ => [_.tenantId, _.productId].join('-')).map(_ => ({ label: _, value: _ }));

  let filteredInventories = inventories;
  if(!isEmpty(requestIdsForFilter)) {
    filteredInventories = filteredInventories.filter(_ => requestIdsForFilter.includes([_.tenantId, _.receivingRequestId || _.combiningRequestId].join('-')));
  }
  if(!isEmpty(productIdsForFilter)) {
    filteredInventories = filteredInventories.filter(_ => productIdsForFilter.includes([_.tenantId, _.productId].join('-')));
  }

  const sortedInventories = orderBy(filteredInventories, _ => _.createdAt.toDate(), 'desc');
  const locations = useCollectionSubscription(locationsRef.orderBy('sortKey'));
  const tenants = useCollectionSubscription(tenantsRef);
  const tenantsById = keyBy(tenants, 'id');
  const tags = useCollectionSubscription(tagsRef.orderBy('createdAt'));
  const tagsById = keyBy(tags, 'id');
  const tenantOptions = tenants.map(_ => ({ label: `[${_.id}] ${_.name}`, value: _.id }));
  const products = useDocumentsFetch(uniqBy(sortedInventories.map(_ => tenantsRef.doc(_.tenantId).collection('products').doc(_.productId)), idWithTenant), [inventories]);
  const productsById = keyBy(products.filter(_ => _), _ => idWithTenant(_.ref));
  const inventoryItems = sortedInventories.map((inventory) => {
    const { id, tenantId, receivingRequestId, productId, } = inventory;
    const tenant = tenantsById[tenantId];
    const product = productsById[tenantId + '-' + productId];
    return {
      ...inventory,
      tenant,
      product,
    };
  });
  const receivingRequestRefOf = ({ tenantId, receivingRequestId, }) => receivingRequestId != null ? tenantsRef.doc(tenantId).collection('receivingRequests').doc(receivingRequestId) : null;
  // NOTE: 入荷依頼が削除済みの場合にbatch.updateがNOT_FOUNDで失敗するため、存在するものだけ更新する
  const fetchExistingReceivingRequestIdWithTenants = async (inventories) => {
    const refs = uniqBy(inventories.map(receivingRequestRefOf).filter(_ => _), idWithTenant);
    const docs = await Promise.all(refs.map(_ => _.get()));
    return new Set(docs.filter(_ => _.exists).map(_ => idWithTenant(_.ref)));
  };

  const onClickDeleteAllDisplayItems = async () => {
    if(!window.confirm('今表示されているものをまとめて削除します。よろしいですか？')) return;

    const existingReceivingRequestIdWithTenants = await fetchExistingReceivingRequestIdWithTenants(inventoryItems);

    await batch(db, inventoryItems, (batch, inventory) => {
      const { id, ref, productId, } = inventory;
      const receivingRequestRef = receivingRequestRefOf(inventory);
      if(receivingRequestRef && existingReceivingRequestIdWithTenants.has(idWithTenant(receivingRequestRef))) {
        batch.update(receivingRequestRef, {
          [`goodReceivedInventoryIds-${productId}`]: firebase.firestore.FieldValue.arrayRemove(id),
          [`ngReceivedInventoryIds-${productId}`]: firebase.firestore.FieldValue.arrayRemove(id),
        });
      }
      batch.delete(ref);
    }, 150); // NOTE: FieldValueを使うと2つ分消費することがあるので
    toast.success('削除しました');
  };

  const validateShelvingRowWithData = (row, rowIndex, receivingDataMap) => {
    const productIdFull = row['商品ID'] || '';
    const receivingIdFull = row['入荷ID'] || '';
    const locationKey = row['ロケキー'] || '';
    const quantity = row['数量'] || '';
    
    const productIdParts = productIdFull.split('-');
    const receivingIdParts = receivingIdFull.split('-');
    const tenantId = productIdParts[0];
    const productId = productIdParts.slice(1).join('-');
    
    const locationsByKey = keyBy(locations, 'sortKey');
    const receivingData = receivingDataMap[receivingIdFull];
    console.log(receivingDataMap, receivingIdFull, receivingData);
    const hasValidProductIdFormat = productIdParts.length >= 2;
    const hasValidReceivingIdFormat = receivingIdParts.length >= 2;
    
    const errors = [
      isEmpty(productIdFull) && '商品IDは必須です',
      !isEmpty(productIdFull) && !hasValidProductIdFormat && '商品IDの形式が正しくありません（テナントID-商品ID形式で入力してください）',
      !isEmpty(productIdFull) && hasValidProductIdFormat && !tenantsById[tenantId] && `テナントID「${tenantId}」が存在しません`,
      isEmpty(locationKey) && 'ロケキーは必須です',
      !isEmpty(locationKey) && !locationsByKey[locationKey] && `ロケキー「${locationKey}」が存在しません`,
      !isEmpty(receivingIdFull) && !hasValidReceivingIdFormat && '入荷IDの形式が正しくありません（テナントID-入荷依頼ID形式で入力してください）',
      !isEmpty(receivingIdFull) && hasValidReceivingIdFormat && !receivingData?.exists && `入荷ID「${receivingIdFull}」が存在しません`,
      !isEmpty(receivingIdFull) && !isEmpty(productIdFull) && receivingData?.exists && hasValidProductIdFormat && !receivingData.items?.map(_ => _.productId).includes(productId) && `入荷ID「${receivingIdFull}」に商品ID「${productIdFull}」が含まれていません`,
      isEmpty(quantity) && '数量は必須です',
      !isEmpty(quantity) && (isNaN(parseInt(quantity, 10)) || parseInt(quantity, 10) < 1) && '数量は1以上の数値で入力してください',
    ].filter(_ => _);

    return { ...row, errors, rowIndex };
  };

  const processShelvingImportRows = async (rows) => {
    // 入荷IDが入力されている行から一意の入荷IDを抽出
    const uniqueReceivingIds = uniq(rows.map(_ => _['入荷ID']).filter(_ => !isEmpty(_) && _.split('-').length >= 2));
    
    // 入荷データを一括取得
    const receivingData = await Promise.all(uniqueReceivingIds.map(async (receivingIdFull) => {
      const parts = receivingIdFull.split('-');
      const tenantId = parts[0];
      const receivingId = parts.slice(1).join('-');
      const receivingRequest = await getDocumentData(db.collection('tenants').doc(tenantId).collection('receivingRequests').doc(receivingId), true);
      return { receivingRequest, receivingIdFull, exists: !!receivingRequest, items: receivingRequest?.items || [] };
    }));
    const receivingDataMap = keyBy(receivingData, 'receivingIdFull');

    const validatedRows = rows.map((row, index) => validateShelvingRowWithData(row, index, receivingDataMap));
    const errorRows = validatedRows.filter(_ => !isEmpty(_.errors));
    
    if(errorRows.length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map(_ => ({
        ...omit(_, ['errors', 'rowIndex']),
        エラー内容: _.errors.join('\n'),
      }))));
      fileDownload(content, 'エラー結果.csv');
      throw new Error('バリデーションエラーがありました。エラー結果.csvをダウンロードしました。');
    }

    const locationsByKey = keyBy(locations, 'sortKey');
    
    await serial(validatedRows, async (row) => {
      const receivingRequestId = row['入荷ID'] || '';
      const productIdFull = row['商品ID'];
      const locationKey = row['ロケキー'];
      const quantity = parseInt(row['数量'], 10);
      const expirationDate = row['期限'] ? new Date(row['期限'] + ' 00:00:00') : null;

      const productIdParts = productIdFull.split('-');
      const tenantId = productIdParts[0];
      const productId = productIdParts.slice(1).join('-');
      const location = locationsByKey[locationKey];

      let receivingRequestRef;

      if(!receivingRequestId) {
        const { docs: [lastReceivingRequestDoc] } = await db.collection('tenants').doc(tenantId).collection('receivingRequests').orderBy('createdAt', 'desc').limit(1).get();
        receivingRequestRef = db.collection('tenants').doc(tenantId).collection('receivingRequests').doc('W' + newId(lastReceivingRequestDoc));
        
        await receivingRequestRef.set({
          items: [{ productId, quantity }],
          productIds: [productId],
          createdAt: new Date(),
          status: 'received',
          receivedAt: new Date(),
          isCreatedBySystem: true,
          specifiedId: '',
          note: '',
          hasLogReceiving: true,
        });

        await db.collection('billingLogs').doc().set({
          type: 'receivingCount',
          tenantId,
          payload: { receivingRequestId: receivingRequestRef.id, hasDocumentNumber: false, quantity: 1 },
          createdAt: new Date(),
        });
      } else {
        const receivingRequestIdParts = receivingRequestId.split('-');
        const receivingTenantId = receivingRequestIdParts[0];
        const receivingId = receivingRequestIdParts.slice(1).join('-');
        receivingRequestRef = db.collection('tenants').doc(receivingTenantId).collection('receivingRequests').doc(receivingId);
      }

      const inventoryDocs = Array(quantity).fill(null).map(_ => inventoriesRef.doc());
      
      await batch(db, inventoryDocs, (batch, inventoryRef) => {
        const shelvedAt = new Date();

        batch.set(inventoryRef, {
          tenantId,
          receivingRequestId: receivingRequestRef.id,
          receivingRequestDocumentNumber: '',
          receivingRequestCheckNote: '',
          productId,
          productBarcodeInfo: productIdFull,
          productBarcodeInfoType: 'id',
          wasNoFnsku: false,
          isNg: false,
          locationId: location.id,
          locationKey: location.sortKey,
          status: 'shelved',
          shelvedAt,
          firstShelvedAt: shelvedAt,
          expirationDate,
          createdAt: new Date(),
        });

        batch.update(receivingRequestRef, {
          [`goodReceivedInventoryIds-${productId}`]: firebase.firestore.FieldValue.arrayUnion(inventoryRef.id),
          [`goodInventoryIds-${productId}`]: firebase.firestore.FieldValue.arrayUnion(inventoryRef.id),
        });

        batch.update(db.collection('tenants').doc(tenantId).collection('products').doc(productId), {
          goodInventoryIds: firebase.firestore.FieldValue.arrayUnion(inventoryRef.id),
        });
      }, 150);
    });
  };

  const onClickDownloadShelvingSampleCsv = () => {
    const data = [
      {
        入荷ID: 'LGP123-W234',
        商品ID: 'LGP123-234',
        ロケキー: 'A01',
        数量: '2',
        期限: '2024/12/31',
      },
      {
        入荷ID: '',
        商品ID: 'LGP456-567', 
        ロケキー: 'B02',
        数量: '1',
        期限: '',
      },
    ];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, '入庫CSVインポート_サンプル.csv');
  };

  return (
    <div className="admin-inventories container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>入庫処理</h4>
        </div>
        <div className="d-flex flex-wrap justify-content-start align-items-end mb-1 gap-1">
          {
            // DEBUG
            // <AddButton label="入庫" opensInitially FormModal={ReceivementFormModal} formProps={{ tenants, locations, }} />
          }
          <AddButton label="入荷検品" FormModal={ReceivementFormModal} formProps={{ tenants, locations, }} />
          <AddButton color="success" iconClassName="fas fa-sign-in-alt" label="入庫" FormModal={DirectShelvingModal} formProps={{ tenants, locations, }} />
          <AddButton color="success" iconClassName="fas fa-sign-in-alt" label="数値入力入庫" FormModal={DirectShelvingModal} formProps={{ tenants, locations, withQuantity: true, }} />
          <AddButton color="success" iconClassName="fas fa-sign-in-alt" label="入荷依頼ID指定入庫" FormModal={DirectShelvingModal} formProps={{ tenants, locations, withQuantity: true, specifiesReceivingRequestId: true, }} />
          <AddButton color="info" iconClassName="fas fa-sign-in-alt" label="セット完成在庫入庫" FormModal={ShelvingModal} formProps={{ tenants, locations, }} />
          <AddButton iconClassName="fas fa-sign-out-alt" label="在庫移動" FormModal={RevertingShelfModal} formProps={{ tenants, locations, }} />
          <AddButton iconClassName="fas fa-sign-out-alt" label="一括在庫移動" FormModal={BatchRevertingShelfModal} formProps={{ tenants, locations, }} />
          <AddButton iconClassName="fas fa-sign-out-alt" label="ロケ検索・移動" FormModal={LocationTransferModal} formProps={{ tenants, locations, }} />
          <Button onClick={onClickDownloadShelvingSampleCsv}>
            <span className="fas fa-download mr-1" />
            入庫サンプルCSVダウンロード
          </Button>
          <ImportButton label="入庫CSVインポート" processRows={processShelvingImportRows} />
        </div>
        <hr className="my-5" />
        <h5>未入庫在庫一覧</h5>
        <div className="d-flex justify-content-start align-items-end mb-3 gap-1">
          <QuerySelector paramName="tenantIds" options={tenantOptions} label="テナントを選択" />
          {tenantIdForFilter && <QuerySelector isMulti paramName="requestIds" options={requestIdOptions} label="依頼IDで絞り込み" />}
          {tenantIdForFilter && <QuerySelector isMulti paramName="productIds" options={productIdOptions} label="商品IDで絞り込み" />}
          <ProgressButton process={onClickDeleteAllDisplayItems} color="danger">
            まとめて削除
          </ProgressButton>
        </div>
        {
          !isMobile() && (
            <div className="mt-4 overflow-scroll position-relative" style={{ zIndex: 0 }}>
              {
                inventoryItems.length > 0 ? (
                  <table className="table sticky-table table-bordered">
                    <thead className="thead-light text-center">
                      <tr className="text-nowrap">
                        <th style={{ minWidth: 150 }}>ID</th>
                        <th style={{ minWidth: 120 }}>状態</th>
                        <th style={{ minWidth: 120 }}>販売不可</th>
                        <th style={{ minWidth: 120 }}>テナントID</th>
                        <th style={{ minWidth: 150 }}>テナント名</th>
                        <th style={{ minWidth: 120 }}>入荷依頼ID</th>
                        <th style={{ minWidth: 120 }}>加工依頼ID</th>
                        <th style={{ minWidth: 120 }}>商品ID</th>
                        <th style={{ minWidth: 150 }}>ロケーションID</th>
                        <th style={{ minWidth: 150 }}>入庫日時</th>
                        <th style={{ minWidth: 150 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        inventoryItems.map((inventoryItem) => {
                          const { id, ref, status, tenantId, tenant, productId, receivingRequestId, combiningRequestId, locationId, createdAt, isNg = false, } = inventoryItem;
                          const { label: statusLabel, color } = statuses[status];
                          // NOTE: 入荷依頼が削除済みの場合にbatch.updateがNOT_FOUNDで失敗するため、存在確認してから更新する
                          const withDeleteBatch = async (batch) => {
                            const receivingRequestRef = receivingRequestRefOf(inventoryItem);
                            if(!receivingRequestRef) return;

                            const existingIdWithTenants = await fetchExistingReceivingRequestIdWithTenants([inventoryItem]);
                            if(!existingIdWithTenants.has(idWithTenant(receivingRequestRef))) return;

                            batch.update(receivingRequestRef, {
                              [`goodReceivedInventoryIds-${productId}`]: firebase.firestore.FieldValue.arrayRemove(id),
                              [`ngReceivedInventoryIds-${productId}`]: firebase.firestore.FieldValue.arrayRemove(id),
                            });
                          };

                          return (
                            <tr key={id} className={classnames(`table-${color}`)}>
                              <td>
                                {id}
                              </td>
                              <td>
                                {statusLabel}
                              </td>
                              <td>
                                {isNg && '販売不可'}
                              </td>
                              <td>
                                {tenant && tenant.id}
                              </td>
                              <td>
                                {tenant && tenant.name}
                                <TenantTags tagIds={tenant?.tagIds} tagsById={tagsById} />
                              </td>
                              <td>
                                {receivingRequestId && [tenantId, receivingRequestId].join('-')}
                              </td>
                              <td>
                                {combiningRequestId && [tenantId, combiningRequestId].join('-')}
                              </td>
                              <td>
                                {[tenantId, productId].join('-')}
                              </td>
                              <td>
                                {locationId}
                              </td>
                              <td>
                                {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                              </td>
                              <td className="text-nowrap">
                                <DeleteButton item={inventoryItem} itemRef={ref} withBatch={withDeleteBatch} />
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                ) : (
                  <div>
                    未入庫の在庫はありません
                  </div>
                )
              }
            </div>
          )
        }
      </div>
    </div>
  );
});
